/**
 * @file Composable for delaying post sorting after reactions
 * It is only called once when surface_reactions store is initialized
 *
 * When a user adds/updates/removes a reaction on a board sorted by reactions:
 * - On non-touchable devices: we defer the re-sort until they hover out to avoid jarring UX on desktop
 * - On touchable devices: we defer the re-sort for 1 after the reaction is added/removed
 *
 * The accumulated reactions for delayed sorting is a frozen copy of the accumulated reactions by wish ID.
 * It is used by surface_post_sorter to sort posts based on reactions (except emoji).
 */
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { isSortingBy } from '@@/bits/surface_settings_helper'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { AccumulatedReactionData, AccumulatedReactions, Id } from '@@/types'
import { computed, ref, watch } from 'vue'

const TOUCHABLE_DELAY_MS = 1000

export function useDelayedReactionSorting(accumulatedReactionsByWishId: { value: AccumulatedReactions }) {
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const globalSnackbarStore = useGlobalSnackbarStore()

  const shouldDeferSortingPostsByReactions = computed<boolean>(() => {
    if (!isAppUsing('delayedReactionSorting')) return false
    return isSortingBy(surfaceWishArrangementStore.originalWishSortBy, 'reactions')
  })

  const postIdTrackedForDelayedReactionSorting = ref<Id | null>(null)
  const postReactionDelayedForSorting = ref<AccumulatedReactionData | null>(null)

  watch(postIdTrackedForDelayedReactionSorting, (newValue, oldValue) => {
    if (oldValue != null && newValue == null) {
      globalSnackbarStore.setSnackbar({
        message: __('Board has been resorted based on your reaction'),
      })
    }
  })

  let touchableDelayTimer: ReturnType<typeof setTimeout> | null = null
  const clearTouchableDelayTimer = (): void => {
    if (touchableDelayTimer != null) {
      clearTimeout(touchableDelayTimer)
      touchableDelayTimer = null
    }
  }

  const startTrackingPostForDelayedReactionSort = (postId: Id, frozenReaction: AccumulatedReactionData): void => {
    clearTouchableDelayTimer()

    postIdTrackedForDelayedReactionSorting.value = postId
    postReactionDelayedForSorting.value = frozenReaction

    if (device.touchable) {
      touchableDelayTimer = setTimeout(() => {
        stopTrackingPostForDelayedReactionSort(postId)
      }, TOUCHABLE_DELAY_MS)
    }
  }

  const stopTrackingPostForDelayedReactionSort = (postId: Id): void => {
    if (postIdTrackedForDelayedReactionSorting.value === postId) {
      clearTouchableDelayTimer()

      postIdTrackedForDelayedReactionSorting.value = null
      postReactionDelayedForSorting.value = null
    }
  }

  watch(postIdTrackedForDelayedReactionSorting, (newValue, oldValue) => {
    if (oldValue != null && newValue == null) {
      globalSnackbarStore.setSnackbar({
        message: __('Board has been sorted based on your reaction'),
      })
    }
  })

  const accumulatedReactionsForDelayedSorting = computed<AccumulatedReactions>(() => {
    const reactions = { ...accumulatedReactionsByWishId.value }
    if (postIdTrackedForDelayedReactionSorting.value != null && postReactionDelayedForSorting.value != null) {
      reactions[postIdTrackedForDelayedReactionSorting.value] = postReactionDelayedForSorting.value
    }
    return reactions
  })

  return {
    shouldDeferSortingPostsByReactions,
    accumulatedReactionsForDelayedSorting,
    startTrackingPostForDelayedReactionSort,
    stopTrackingPostForDelayedReactionSort,
  }
}
